/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-use-before-define */
import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/HighlightOffOutlined';
import { getFromLS, getHeaders, makeRequest } from '@orientaction/utils';
import React, { useCallback, useEffect, useState } from 'react';
import config from '../../services/config';

const useStyles = makeStyles(theme => ({
  drawerContent: {
    width: '60%',
    borderTop: '2px solid #23296F',
    borderRadius: 16,
  },
  closeBtn: {
    position: 'absolute',
    width: 50,
    height: 50,
    right: '0.001rem',
    top: '0.001rem',
    color: '#fff',
  },
  contentChild: {
    paddingLeft: 30,
    paddingRight: 30,
    paddingTop: 30,
    paddingBottom: 30,
  },
  title: {
    lineHeight: '30px',
    color: '#fff',
    textTransform: 'none',
    marginBottom: 20,
    fontFamily: 'Poppins',
    fontSize: 18,
    fontWeight: 700,
  },
  rootStyle: {
    background: '#000a8c6b',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.light,
    borderBottom: '2px solid #23296F',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  titleContainer: {
    marginTop: 25,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#000657',
    marginBottom: 16,
  },
  testTitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 24,
  },
  statsContainer: {
    marginBottom: 32,
  },
  statCard: {
    backgroundColor: '#000657',
    color: '#fff',
    padding: '20px',
    borderRadius: 8,
    textAlign: 'center',
    minHeight: 100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: 400,
  },
  tableContainer: {
    marginTop: 16,
  },
  viewCommentButton: {
    textDecoration: 'underline',
    color: theme.palette.primary.main,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  analyticsStarsContainer: {
    display: 'flex',
    gap: 2,
  },
  analyticsStar: {
    color: '#FFD700',
    fontSize: 16,
  },
  analyticsStarEmpty: {
    color: '#D4D4D8',
    fontSize: 16,
  },
}));

interface AnalyticsDrawerProps {
  open: boolean;
  testId: number;
  testTitle: string;
  onClose: () => void;
}

interface TestReview {
  id: number;
  user?: {
    username: string;
    email: string;
    phone: string;
    firstname?: string;
    lastname?: string;
    rattachement?: boolean;
    upper_hierarchy?: any;
    company?: any;
    service?: any;
  };
  note: number;
  comment: string;
  createdAt: string;
}

const AnalyticsDrawer: React.FC<AnalyticsDrawerProps> = ({ open, testId, testTitle, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [testData, setTestData] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(12);
  const [selectedReview, setSelectedReview] = useState<TestReview | null>(null);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);

  const token = getFromLS('userToken') || '';

  const fetchData = useCallback(async () => {
    if (!open || !testId) return;

    setLoading(true);
    try {
      // Récupérer les données du test
      const testResponse = await makeRequest(
        `${config.api_url}/api/tests/${testId}/analytics`,
        'GET',
        {},
        getHeaders(token)
      );
      setTestData(testResponse?.data || {});

      // Récupérer les reviews
      const reviewsResponse = await makeRequest(
        `${config.api_url}/api/tests/${testId}/reviews?page=${page}&limit=${rowsPerPage}&populate[user][fields][0]=username&populate[user][fields][1]=email&populate[user][fields][2]=phone&populate[user][fields][3]=firstname&populate[user][fields][4]=lastname&populate[user][fields][5]=rattachement&populate[user][populate][0]=service&populate[user][populate][1]=upper_hierarchy&populate[user][populate][2]=company`,
        'GET',
        {},
        getHeaders(token)
      );
      // Correction : reviews doit toujours être un tableau
      let reviewsData: any[] = [];
      if (Array.isArray(reviewsResponse?.data)) {
        reviewsData = reviewsResponse.data;
      } else if (Array.isArray(reviewsResponse?.data?.data)) {
        reviewsData = reviewsResponse.data.data;
      }
      setReviews(reviewsData);
      setTotalReviews(reviewsResponse?.data?.meta?.pagination?.total || reviewsData.length);
    } catch (error) {
      setTestData({});
      setReviews([]);
      setTotalReviews(0);
    } finally {
      setLoading(false);
    }
  }, [open, testId, token, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleViewComment = (review: TestReview) => {
    setSelectedReview(review);
    setCommentDrawerOpen(true);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        // Étoile pleine (jaune)
        stars.push(
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 15 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: 2 }}>
            <path
              d="M7.50065 0.333984L9.56065 4.50732L14.1673 5.18065L10.834 8.42732L11.6207 13.014L7.50065 10.8473L3.38065 13.014L4.16732 8.42732L0.833984 5.18065L5.44065 4.50732L7.50065 0.333984Z"
              fill="#FACC15"
            />
          </svg>
        );
      } else {
        // Étoile vide (grise)
        stars.push(
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 15 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: 2 }}>
            <path
              d="M7.50065 0.333984L9.56065 4.50732L14.1673 5.18065L10.834 8.42732L11.6207 13.014L7.50065 10.8473L3.38065 13.014L4.16732 8.42732L0.833984 5.18065L5.44065 4.50732L7.50065 0.333984Z"
              fill="#D4D4D8"
            />
          </svg>
        );
      }
    }
    return stars;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return 'Date invalide';
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ root: classes.rootStyle, paper: classes.drawerContent }}>
        <div className={classes.headerContainer}>
          <IconButton className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <div className={classes.titleContainer}>
            <Typography variant="h3" className={classes.title}>
              Données analytiques
            </Typography>
          </div>
        </div>

        <div className={classes.contentChild}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Typography variant="h6" className={classes.sectionTitle}>
                Titre du test
              </Typography>
              <Typography className={classes.testTitle}>
                {testData?.title || testTitle || 'Titre non disponible'}
              </Typography>

              <div className={classes.statsContainer}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Statistiques
                </Typography>
                <Grid container={true} spacing={3}>
                  <Grid item={true} xs={4}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statValue}>
                        {testData?.data?.passationsCount || 0}
                      </Typography>
                      <Typography className={classes.statLabel}>Nb. Passations</Typography>
                    </Card>
                  </Grid>
                  <Grid item={true} xs={4}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statValue}>
                        {testData?.data?.noteAverage
                          ? `${testData.data.noteAverage.toFixed(2)} / 5`
                          : '0 / 5'}
                      </Typography>
                      <Typography className={classes.statLabel}>Note moyenne</Typography>
                    </Card>
                  </Grid>
                  <Grid item={true} xs={4}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statValue}>
                        {(() => {
                          const timeInSeconds = testData?.data?.timeAverage || 0;
                          if (timeInSeconds < 600) {
                            return `${Math.round(timeInSeconds * 10) / 10}s`;
                          }
                          const timeInMinutes = Math.round(timeInSeconds / 60);
                          return `${timeInMinutes}min`;
                        })()}
                      </Typography>
                      <Typography className={classes.statLabel}>Temps passé moyen</Typography>
                    </Card>
                  </Grid>
                </Grid>
              </div>

              <Typography variant="h6" className={classes.sectionTitle}>
                Commentaires des utilisateurs
              </Typography>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#000657' }}>
                      <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                        Utilisateurs
                      </TableCell>
                      <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell style={{ color: 'white', fontWeight: 'bold' }}>
                        Note de l'utilisateur
                      </TableCell>
                      <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(reviews) && reviews.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Aucun commentaire disponible
                        </TableCell>
                      </TableRow>
                    ) : (
                      (Array.isArray(reviews) ? reviews : []).map((review, index: number) => {
                        const reviewData = review.attributes || review;
                        const userData = reviewData.user?.data?.attributes || reviewData.user;
                        return (
                        <TableRow
                          key={
                            (review.id ||
                            reviewData.id ||
                            `${reviewData.createdAt}-${userData?.id || userData?.username || reviewData.note}`) ?? index
                          }>
                          <TableCell style={{ color: '#000657', fontWeight: 'bold' }}>
                              {userData?.username || 'Utilisateur inconnu'}
                          </TableCell>
                            <TableCell>{formatDate(reviewData.createdAt)}</TableCell>
                            <TableCell>{renderStars(reviewData.note)}</TableCell>
                          <TableCell>
                            <button
                              type="button"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#000657',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                              }}
                                onClick={() =>
                                  handleViewComment({
                                    ...reviewData,
                                    user: userData,
                                  })
                                }>
                              Voir le commentaire
                            </button>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[12]}
                  component="div"
                  count={totalReviews}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                />
              </TableContainer>
            </>
          )}
        </div>
      </Drawer>

      {/* Comment Detail Drawer */}
      <Drawer anchor="right" open={commentDrawerOpen} onClose={() => setCommentDrawerOpen(false)}>
        <div style={{ width: 800, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header gris comme avant mais même hauteur */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0px 24px 0px 24px',
              backgroundColor: '#f5f5f5',
              height: '77px',
            }}>
            <Typography variant="h6" style={{ color: '#000657', fontWeight: 'bold' }}>
              Commentaire
            </Typography>
            <IconButton onClick={() => setCommentDrawerOpen(false)}>
              <img
                src="/images/svg/arrow_right.svg"
                alt="arrow"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </div>

          {/* Séparateur fin */}
          <div style={{ height: 2, backgroundColor: '#23296F' }} />

          {/* Content - prend toute la hauteur restante */}
          <div style={{ padding: 24, backgroundColor: 'white', flex: 1 }}>
            {selectedReview && (
              <>
                {/* Information sur le commentaire */}
                <Typography
                  variant="h6"
                  style={{ color: '#000657', fontWeight: 'bold', marginBottom: 16 }}>
                  Information sur le commentaire
                </Typography>

                {/* 3 colonnes layout */}
                <Grid container={true} spacing={3} style={{ marginBottom: 32 }}>
                  {/* Utilisateur */}
                  <Grid item={true} xs={4}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                      UTILISATEUR
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: '#000657', fontWeight: 'bold', marginBottom: 8 }}>
                      {selectedReview.user?.firstname && selectedReview.user?.lastname
                        ? `${selectedReview.user.firstname} ${selectedReview.user.lastname}`
                        : 'Nom non disponible'}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <img
                        src="/images/svg/letter.svg"
                        alt="email"
                        style={{ width: 16, height: 16, marginRight: 8 }}
                      />
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {selectedReview.user?.email || 'Email non disponible'}
                      </Typography>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src="/images/svg/phone.svg"
                        alt="phone"
                        style={{ width: 16, height: 16, marginRight: 8 }}
                      />
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {selectedReview.user?.phone || 'Téléphone non disponible'}
                      </Typography>
                    </div>
                  </Grid>

                  {/* Note de l'utilisateur */}
                  <Grid item={true} xs={4}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                      NOTE DE L'UTILISATEUR
                    </Typography>
                    <div>{renderStars(selectedReview.note)}</div>
                  </Grid>

                  {/* Statut */}
                  <Grid item={true} xs={4}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                      STATUT
                    </Typography>
                    <Typography variant="body2" style={{ color: '#000657' }}>
                      {selectedReview.user?.service?.name ||
                        selectedReview.user?.service?.attributes?.name ||
                        'Non-rattaché'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Commentaire */}
                <Typography
                  variant="h6"
                  style={{ color: '#000657', fontWeight: 'bold', marginBottom: 16, marginTop: 64 }}>
                  Commentaire
                </Typography>
                <Typography variant="body1" style={{ lineHeight: 1.6, color: '#333' }}>
                  {selectedReview.comment || 'Aucun commentaire disponible'}
                </Typography>
              </>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

// Composant du drawer de détail du commentaire
const CommentDrawer: React.FC<{
  open: boolean;
  review: TestReview | null;
  onClose: () => void;
}> = ({ open, review, onClose }) => {
  const classes = useStyles();

  const renderStars = (rating: number) => {
    const stars = [];
    const safeRating = Math.max(0, Math.min(5, rating || 0));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={i <= safeRating ? classes.analyticsStar : classes.analyticsStarEmpty}>
          ★
        </span>
      );
    }
    return <div className={classes.analyticsStarsContainer}>{stars}</div>;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ root: classes.rootStyle, paper: classes.drawerContent }}>
      <div className={classes.headerContainer}>
        <IconButton className={classes.closeBtn} onClick={onClose}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
        <div className={classes.titleContainer}>
          <Typography variant="h3" className={classes.title}>
            Commentaire
          </Typography>
        </div>
      </div>

      {review && (
        <div className={classes.contentChild}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Information sur le commentaire
          </Typography>

          <div style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Utilisateur
            </Typography>
            <Typography>{review.user?.username || 'Utilisateur inconnu'}</Typography>
            <Typography style={{ fontSize: 14, color: '#666' }}>
              {review.user?.email || 'Email non disponible'}
            </Typography>
            <Typography style={{ fontSize: 14, color: '#666' }}>
              {review.user?.phone || 'Téléphone non disponible'}
            </Typography>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Note de l&apos;utilisateur
            </Typography>
            {renderStars(review.note)}
          </div>

          <div style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Statut
            </Typography>
            <Typography>
              {review.user?.service?.name ||
                review.user?.service?.attributes?.name ||
                'Non-rattaché'}
            </Typography>
          </div>

          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Commentaire
            </Typography>
            <Typography style={{ lineHeight: 1.6 }}>
              {review.comment || 'Aucun commentaire'}
            </Typography>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default AnalyticsDrawer;
